import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { APP, ROUTES, isPublicHost, sanitiseOrgCode, workspaceHostUrl } from '../config';
import { AuthAPI } from '../lib/api';
import { ErrorBox } from '../components/Loader.jsx';

const ORGANISATION_CODE_HINT = 'Enter Organisation Code eg: Apple';

function BrandMark() {
  return (
    <div
      aria-hidden
      style={{
        width: 64,
        height: 64,
        margin: '0 auto 14px',
        borderRadius: 18,
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        fontSize: 28,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
        boxShadow: '0 12px 28px rgba(121, 128, 255, 0.35)',
      }}
    >
      {(APP.name || 'T').slice(0, 1).toUpperCase()}
    </div>
  );
}

function OkBanner({ children }) {
  return (
    <div className="ty-ok" role="status">
      {children}
    </div>
  );
}

/**
 * Public password recovery.
 *
 * Two modes share this screen:
 *   * Request — visitor types their email (and, on the apex host, an
 *     organisation code to jump to the right workspace).
 *   * Confirm — the emailed ``?uid=&token=`` link; they choose a new
 *     password.
 *
 * Recovery is tenant-scoped the same way login is: a reset requested on
 * ``acme.tasky.do`` can only reset an account in that schema.
 */
export default function PasswordRecoveryPage() {
  const [searchParams] = useSearchParams();
  const onApex = isPublicHost();

  const uid = (searchParams.get('uid') || '').trim();
  const token = (searchParams.get('token') || '').trim();
  const isConfirm = Boolean(uid && token);

  const [organisation, setOrganisation] = useState(searchParams.get('organisation') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [updated, setUpdated] = useState(false);

  const loginTo = useMemo(() => {
    const params = new URLSearchParams();
    if (email.trim()) params.set('email', email.trim());
    const search = params.toString();
    return search ? `${ROUTES.LOGIN}?${search}` : ROUTES.LOGIN;
  }, [email]);

  const onRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const slug = sanitiseOrgCode(organisation);
      if (onApex && slug) {
        const params = new URLSearchParams();
        if (email.trim()) params.set('email', email.trim());
        const search = params.toString();
        const path = search
          ? `${ROUTES.PASSWORD_RECOVERY}?${search}`
          : ROUTES.PASSWORD_RECOVERY;
        window.location.assign(workspaceHostUrl(slug, path));
        return;
      }
      if (!email.trim()) {
        setError(new Error('Enter your email address.'));
        return;
      }
      await AuthAPI.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    if (password.length < 4) {
      setError(new Error('New password must be at least 4 characters.'));
      setSubmitting(false);
      return;
    }
    if (password !== confirm) {
      setError(new Error('New password and confirmation do not match.'));
      setSubmitting(false);
      return;
    }
    try {
      await AuthAPI.confirmPasswordReset(uid, token, password);
      setUpdated(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ty-login">
      <div className="ty-login__card">
        <BrandMark />
        <h1 className="ty-login__title">
          {isConfirm ? 'Choose a new password' : 'Forgot password'}
        </h1>
        <p className="ty-login__sub">
          {isConfirm
            ? 'Enter a new password for your account.'
            : onApex
              ? 'Recover a platform account here, or enter your organisation code to reset a workspace password.'
              : 'Enter the email on your account. If it matches, we will send recovery instructions.'}
        </p>

        {isConfirm ? (
          updated ? (
            <>
              <OkBanner>Password updated. You can sign in with your new password.</OkBanner>
              <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
                <Link to={ROUTES.LOGIN} className="ty-link">
                  Back to sign in
                </Link>
              </p>
            </>
          ) : (
            <form onSubmit={onConfirm}>
              <ErrorBox error={error} />
              <div className="ty-field">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  className="ty-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={4}
                />
              </div>
              <div className="ty-field">
                <label htmlFor="confirm-password">Confirm new password</label>
                <input
                  id="confirm-password"
                  className="ty-input"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={4}
                />
              </div>
              <button className="ty-btn" type="submit" disabled={submitting} style={{ width: '100%' }}>
                {submitting ? 'Updating…' : 'Update password'}
              </button>
              <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
                <Link to={ROUTES.LOGIN} className="ty-link">
                  Back to sign in
                </Link>
              </p>
            </form>
          )
        ) : sent ? (
          <>
            <OkBanner>
              If an account exists for that email, we have sent password recovery
              instructions. Check your inbox and spam folder.
            </OkBanner>
            <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
              <Link to={loginTo} className="ty-link">
                Back to sign in
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={onRequest}>
            {onApex ? (
              <div className="ty-field">
                <label htmlFor="organisation">Organisation Code</label>
                <input
                  id="organisation"
                  className="ty-input"
                  type="text"
                  autoComplete="organization"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  value={organisation}
                  onChange={(e) => setOrganisation(e.target.value)}
                  placeholder={ORGANISATION_CODE_HINT}
                />
              </div>
            ) : null}
            <ErrorBox error={error} />
            <div className="ty-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                className="ty-input"
                type="email"
                autoComplete="username"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={!onApex}
              />
            </div>
            <button className="ty-btn" type="submit" disabled={submitting} style={{ width: '100%' }}>
              {submitting
                ? 'Sending…'
                : onApex && sanitiseOrgCode(organisation)
                  ? 'Continue to workspace'
                  : 'Send recovery email'}
            </button>
            <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
              Remembered it?{' '}
              <Link to={loginTo} className="ty-link">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
